import React, { useEffect, useRef, useState } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import { Save, Eye, Loader2, FileSignature } from "lucide-react";
import { getBaseTemplate, updateBaseTemplate } from "../../services/esign";
import { DOCUMENT_VARIABLES } from "../Projects/documentVariables";
import PageHeader from "../../components/PageHeader";

// Emit alignment as an inline style (not a `ql-align-*` class) so the generated PDF renders it
// without needing Quill's stylesheet. Color/background are already inline by default.
const AlignStyle = Quill.import("attributors/style/align");
Quill.register(AlignStyle, true);

const DOC_TYPE = "INTERIOR_EXECUTION_AGREEMENT";
const DOC_TYPE_BV = "INTERIOR_EXECUTION_AGREEMENT_BV";
const TEMPLATE_TYPES = [
  { key: DOC_TYPE, label: "Developer + BuildVizer + Buyer" },
  { key: DOC_TYPE_BV, label: "BuildVizer + Buyer (developer-less)" },
];

const MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, 4, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    ["blockquote", "link"],
    ["clean"],
  ],
};


/**
 * Zumale-admin editor for the Interior Design & Execution Agreement base template. This is the
 * SOURCE OF TRUTH for the agreement HTML (the Drive sync is configured to skip this type). Values
 * stay as {tokens}; the backend fills them at generation. Full rich-text formatting via Quill.
 */
const AgreementTemplate = () => {
  const quillRef = useRef(null);
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [docType, setDocType] = useState(DOC_TYPE);

  const VARIABLES = DOCUMENT_VARIABLES[docType] || DOCUMENT_VARIABLES[DOC_TYPE] || [];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getBaseTemplate(docType);
        if (!cancelled) setHtml(res?.html || "");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load the agreement template");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [docType]);

  const insertToken = (token) => {
    if (!token) return;
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const range = editor.getSelection(true);
    const index = range ? range.index : editor.getLength();
    editor.insertText(index, token, "user");
    editor.setSelection(index + token.length, 0);
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateBaseTemplate(docType, html);
      toast.success("Agreement template saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the agreement template");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 flex flex-col gap-6 min-h-[calc(100vh-4rem)]">
      <PageHeader
        icon={FileSignature}
        title="Interior Design & Execution Agreement"
        subtitle="Master template — source of truth for the agreement PDF. Keep {tokens} intact; they are filled per unit at generation."
        actions={
          <>
            <button
              onClick={() => setPreviewOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-white/30 px-3 py-2 text-sm font-medium text-white hover:bg-white/10 transition-colors"
            >
              <Eye size={15} /> Preview
            </button>
            <button
              onClick={save}
              disabled={saving || loading}
              className="inline-flex items-center gap-1.5 rounded-lg bg-flamingo-500 px-3 py-2 text-sm font-medium text-white hover:bg-flamingo-600 disabled:opacity-50 transition-colors"
            >
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
            </button>
          </>
        }
      />

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 lg:p-5 flex flex-col gap-3">
        {/* Template variant toggle */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-slate-500">Template:</span>
          {TEMPLATE_TYPES.map((t) => (
            <button
              key={t.key}
              onClick={() => setDocType(t.key)}
              disabled={saving}
              className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
                docType === t.key
                  ? "bg-flamingo-600 text-white border-flamingo-600"
                  : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Keyword inserter */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-slate-500">Insert field:</span>
          <select
            defaultValue=""
            onChange={(e) => {
              insertToken(e.target.value);
              e.target.value = "";
            }}
            className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-700 focus:border-flamingo-500 focus:outline-none"
          >
            <option value="" disabled>
              Choose a field…
            </option>
            {VARIABLES.map((v) => (
              <option key={v.value} value={v.value}>
                {v.label}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-slate-500">
            <Loader2 size={16} className="animate-spin" /> Loading template…
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200">
            <ReactQuill ref={quillRef} theme="snow" value={html} onChange={setHtml} modules={MODULES} />
          </div>
        )}
      </div>

      {previewOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
          <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
              <h3 className="text-sm font-semibold text-slate-800">Template preview (tokens shown literally)</h3>
              <button onClick={() => setPreviewOpen(false)} className="text-slate-400 hover:text-slate-600">
                Close
              </button>
            </div>
            <iframe title="Template preview" srcDoc={html} className="h-[70vh] w-full border-0" />
          </div>
        </div>
      )}
    </div>
  );
};

export default AgreementTemplate;
